
import React from 'react';
import { Consultancy } from '../../types';
import { Check, X, Shield, AlertCircle } from 'lucide-react';

interface Props {
    consultancies: Consultancy[];
}

export const ComparisonTable: React.FC<Props> = ({ consultancies }) => {
    if (!consultancies || consultancies.length === 0) {
        return (
            <div className="bg-white p-8 rounded-lg border border-slate-200 text-center text-slate-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p>No consultancies selected for comparison.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/4">Feature</th>
                        {consultancies.map(c => (
                            <th key={c.id} className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                <div className="flex flex-col items-start gap-2">
                                    <img src={c.logoUrl} alt={c.name} className="w-10 h-10 rounded object-cover border border-slate-200"/>
                                    <span className="text-slate-900 font-bold text-sm">{c.name}</span>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                     <tr>
                         <td className="px-6 py-4 font-medium text-slate-900 text-sm">Verified Approvals</td>
                         {consultancies.map(c => (
                             <td key={c.id} className="px-6 py-4 text-slate-700 text-sm">
                                 <div className="flex items-center gap-1 font-bold text-slate-900">
                                     <Shield className="w-4 h-4 text-green-600" /> {c.verifiedApprovals}
                                 </div>
                             </td>
                         ))}
                     </tr>
                     <tr>
                         <td className="px-6 py-4 font-medium text-slate-900 text-sm">Orphan Designations</td>
                         {consultancies.map(c => (
                             <td key={c.id} className="px-6 py-4 text-slate-700 text-sm">{c.orphanDesignations}</td>
                         ))}
                     </tr>
                     <tr>
                         <td className="px-6 py-4 font-medium text-slate-900 text-sm">Hourly Rate</td>
                         {consultancies.map(c => (
                             <td key={c.id} className="px-6 py-4 text-slate-700 text-sm">
                                 ${c.hourlyRateStart} - ${c.hourlyRateEnd}
                             </td>
                         ))}
                     </tr>
                     <tr>
                         <td className="px-6 py-4 font-medium text-slate-900 text-sm">Availability</td>
                         {consultancies.map(c => (
                             <td key={c.id} className="px-6 py-4 text-slate-700 text-sm">
                                 <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.availability === 'Immediate' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                                     {c.availability}
                                 </span>
                             </td>
                         ))}
                     </tr>
                     <tr>
                         <td className="px-6 py-4 font-medium text-slate-900 text-sm">Team Size</td>
                         {consultancies.map(c => (
                             <td key={c.id} className="px-6 py-4 text-slate-700 text-sm">{c.teamSize}</td>
                         ))}
                     </tr>
                     <tr>
                         <td className="px-6 py-4 font-medium text-slate-900 text-sm">Specialties</td>
                         {consultancies.map(c => (
                             <td key={c.id} className="px-6 py-4 text-slate-700 text-sm">
                                 <div className="flex flex-wrap gap-1">
                                     {c.badges.slice(0, 3).map(b => (
                                         <span key={b} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">{b}</span>
                                     ))}
                                 </div>
                             </td>
                         ))}
                     </tr>
                </tbody>
            </table>
        </div>
    );
};
